import { ref } from 'vue'
import { fetchRecipientCount } from '@/api/newsletters'
import type { RecipientCount, UpsertAudiencePayload } from '@/types/newsletter'

const DEBOUNCE_MS = 600

export function useRecipientCount(organisationId: number) {
  const count = ref<RecipientCount | null>(null)
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  async function refresh(newsletterId: number, params?: Partial<UpsertAudiencePayload>): Promise<void> {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(async () => {
      loading.value = true
      error.value = null
      try {
        count.value = await fetchRecipientCount(organisationId, newsletterId, params)
      } catch {
        error.value = 'Could not refresh recipient count.'
      } finally {
        loading.value = false
      }
    }, DEBOUNCE_MS)
  }

  async function refreshNow(newsletterId: number, params?: Partial<UpsertAudiencePayload>): Promise<void> {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    loading.value = true
    error.value = null
    try {
      count.value = await fetchRecipientCount(organisationId, newsletterId, params)
    } catch {
      error.value = 'Could not refresh recipient count.'
    } finally {
      loading.value = false
    }
  }

  return { count, loading, error, refresh, refreshNow }
}
